import { useState, useEffect } from 'react';
import { useI18n } from '../../../../i18n';
import { request } from '../../../shared/services/api';
import { useRequestsConfig } from '../hooks/useRequestsConfig';
import type { NotificacionPlantilla, TipoSolicitud } from '../types/requests.types';

const EVENTOS = [
  'SOLICITUD_CREADA',
  'PASO_PENDIENTE',
  'PASO_APROBADO',
  'PASO_RECHAZADO',
  'SOLICITUD_COMPLETADA',
  'SOLICITUD_CANCELADA',
  'ESCALADO_ACTIVADO',
] as const;

interface PlantillaForm {
  evento: string;
  asunto: string;
  cuerpoHtml: string;
  tipoSolicitudId: string;
  activo: boolean;
}

const EMPTY_FORM: PlantillaForm = {
  evento: '',
  asunto: '',
  cuerpoHtml: '',
  tipoSolicitudId: '',
  activo: true,
};

export function PlantillasTab() {
  const { t } = useI18n();
  const { items, loading, error, crear, actualizar, eliminar, refetch } =
    useRequestsConfig<NotificacionPlantilla>('/requests/plantillas-notificacion');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<PlantillaForm>(EMPTY_FORM);
  const [tipos, setTipos] = useState<TipoSolicitud[]>([]);

  useEffect(() => {
    request<{ data: TipoSolicitud[] }>('/requests/tipos')
      .then((res) => setTipos(res.data))
      .catch(() => setTipos([]));
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (item: NotificacionPlantilla) => {
    setEditId(item.id);
    setForm({
      evento: item.evento,
      asunto: item.asunto,
      cuerpoHtml: item.cuerpoHtml,
      tipoSolicitudId: item.tipoSolicitudId ?? '',
      activo: item.activo,
    });
    setShowModal(true);
  };

  const close = () => setShowModal(false);

  const save = async () => {
    const payload = {
      ...form,
      tipoSolicitudId: form.tipoSolicitudId || null,
    };
    if (editId) {
      await actualizar(editId, payload);
    } else {
      await crear(payload);
    }
    close();
  };

  const handleDelete = async (id: string) => {
    await eliminar(id);
    refetch();
  };

  if (loading) {
    return <div style={{ padding: 32, color: '#6B7280' }}>{t.requests.cargando}</div>;
  }

  if (error) {
    return <div style={{ padding: 32, color: '#EF4444' }}>{error}</div>;
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 4,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #D1D5DB',
    fontSize: 14,
    outline: 'none',
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F3864', margin: 0 }}>
          {t.requests?.plantillas ?? 'Plantillas de notificaci\u00f3n'}
        </h3>
        <button
          onClick={openNew}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#1F3864',
            color: '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {t.requests?.nuevaPlantilla ?? 'Nueva plantilla'}
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.evento ?? 'Evento'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.asunto ?? 'Asunto'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.tipoSolicitud ?? 'Tipo solicitud'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.activo ?? 'Activo'}
            </th>
            <th
              style={{
                textAlign: 'right',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.acciones ?? 'Acciones'}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: '#EFF6FF',
                    color: '#1F3864',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {item.evento}
                </span>
              </td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>{item.asunto}</td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                {item.tipoSolicitudId
                  ? (tipos.find((tp) => tp.id === item.tipoSolicitudId)?.nombre ?? '-')
                  : (t.requests?.todos ?? 'Todos')}
              </td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: item.activo ? '#22C55E' : '#D1D5DB',
                  }}
                />
              </td>
              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                <button
                  onClick={() => openEdit(item)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2F5496',
                    cursor: 'pointer',
                    marginRight: 8,
                    fontSize: 13,
                  }}
                >
                  {t.requests?.editar ?? 'Editar'}
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#EF4444',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  {t.requests?.eliminar ?? 'Eliminar'}
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td
                colSpan={5}
                style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: 14,
                }}
              >
                {t.requests?.sinPlantillas ?? 'No hay plantillas de notificaci\u00f3n configuradas'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 480,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F3864', margin: '0 0 20px' }}>
              {editId
                ? (t.requests?.editarPlantilla ?? 'Editar plantilla')
                : (t.requests?.nuevaPlantilla ?? 'Nueva plantilla')}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>{t.requests?.evento ?? 'Evento'}</label>
                <select
                  value={form.evento}
                  onChange={(e) => setForm({ ...form, evento: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">{t.requests?.seleccionar ?? 'Seleccionar...'}</option>
                  {EVENTOS.map((ev) => (
                    <option key={ev} value={ev}>
                      {ev}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>{t.requests?.asunto ?? 'Asunto'}</label>
                <input
                  type="text"
                  value={form.asunto}
                  onChange={(e) => setForm({ ...form, asunto: e.target.value })}
                  style={inputStyle}
                  placeholder={
                    t.requests?.asuntoPlaceholder ?? 'Asunto del correo de notificaci\u00f3n'
                  }
                />
              </div>

              <div>
                <label style={labelStyle}>{t.requests?.cuerpoHtml ?? 'Cuerpo HTML'}</label>
                <textarea
                  value={form.cuerpoHtml}
                  onChange={(e) => setForm({ ...form, cuerpoHtml: e.target.value })}
                  rows={8}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    fontFamily: 'monospace',
                    fontSize: 13,
                  }}
                  placeholder="<p>{{solicitante}}, su solicitud {{codigo}} ha sido...</p>"
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t.requests?.tipoSolicitud ?? 'Tipo solicitud'} (
                  {t.requests?.opcional ?? 'opcional'})
                </label>
                <select
                  value={form.tipoSolicitudId}
                  onChange={(e) => setForm({ ...form, tipoSolicitudId: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">{t.requests?.todos ?? 'Todos los tipos'}</option>
                  {tipos.map((tp) => (
                    <option key={tp.id} value={tp.id}>
                      {tp.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  id="plantilla-activo"
                />
                <label htmlFor="plantilla-activo" style={{ fontSize: 14, color: '#374151' }}>
                  {t.requests?.activo ?? 'Activo'}
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={close}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={save}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#1F3864',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.common.guardar}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
